import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles, Chip, SelectWrapper } from '@Sage/hoi-poi-ui';
import AgentsIcon from '../../../CopilotSidebar/components/AgentsIcon/Agents';
import { CopilotContext } from '../../../Context';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotAgentSwitcher');

const AgentSwitcher = ({ chatId }) => {
    const [state, setState] = useState({
        showPopover: false,
    });
    const popoverRef = useRef(null);
    const classes = useStyles();

    const { chats, copilotAgents, defaultAgent, setActiveAgent } = useContext(CopilotContext);
    const chatState = useMemo(() => chats?.[chatId] ?? {}, [chats, chatId]);
    const { activeAgent } = chatState;

    const currentAgent = useMemo(() => activeAgent || defaultAgent, [activeAgent, defaultAgent]);

    const onChange = useCallback(
        (payload) => {
            const newActiveAgent = copilotAgents.find((agent) => agent.name === payload.value);
            if (newActiveAgent) {
                setActiveAgent({ chatId, agent: newActiveAgent });
            }
            setState((state) => ({ ...state, showPopover: false }));
        },
        [chatId, copilotAgents, setActiveAgent],
    );

    const chipProps = useMemo(
        () => ({
            icon: <AgentsIcon />,
            isUnfolded: true,
            isOutlined: true,
            onClick: () => setState((state) => ({ ...state, showPopover: true })),
            children: (
                <span className={classes.chipContent}>
                    <AgentsIcon className={classes.chipIcon} />
                    <span>
                        {currentAgent?.label || currentAgent?.name || currentAgent?.description}
                    </span>
                </span>
            ),
        }),
        [currentAgent, classes.chipContent, classes.chipIcon],
    );

    const selectWrapperProps = useMemo(
        () => ({
            value: currentAgent?.name,
            onChange,
            options: copilotAgents.map((agent) => ({
                label: agent?.label || agent?.name,
                value: agent.name,
            })),
            children: <Chip {...chipProps} />,
            getPopoverContentRef: (ref) => {
                if (ref) popoverRef.current = ref;
            },
            overrides: { Popover: { root: { visible: state.showPopover, placement: 'topLeft' } } },
        }),
        [currentAgent, chipProps, copilotAgents, onChange, state.showPopover],
    );

    const handleClickOutside = useCallback((event) => {
        if (!popoverRef.current?.contains?.(event.target)) {
            setState((state) => ({ ...state, showPopover: false }));
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);

    if (selectWrapperProps.options.length === 0) return null;

    return <SelectWrapper {...selectWrapperProps} />;
};

export default memo(AgentSwitcher);
